import React, { useEffect, useMemo, useState } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { Bell, Check, Clock, Repeat, X } from 'lucide-react';
import type { TaskReminder, TaskReminderKind } from '../../types';
import { flushDueTaskReminders, isTaskReminderInFuture } from '../../utils/taskReminderApi';
import {
  formatRecurringIntervalLabel,
  TASK_REMINDER_PRESETS,
  TASK_REMINDER_RECURRING_INTERVALS,
} from '../../utils/taskReminderValidation';
import './TaskReminderSection.css';

const CUSTOM_PRESET_ID = 'custom';
const SUCCESS_DISMISS_MS = 5000;

interface TaskReminderSectionProps {
  taskId: string;
  projectId: string;
  taskTitle: string;
  currentUserId: string;
  reminders: TaskReminder[];
  onCreateReminder: (
    remindAt: Date,
    message?: string,
    opts?: { kind?: TaskReminderKind; intervalMinutes?: number },
  ) => void | Promise<void>;
  onCancelReminder: (reminderId: string) => void | Promise<void>;
}

function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function defaultPresetId(): string {
  return TASK_REMINDER_PRESETS.find((p) => p.id === '1d')?.id ?? TASK_REMINDER_PRESETS[0]?.id ?? CUSTOM_PRESET_ID;
}

function resolvePresetDatetime(presetId: string): Date | null {
  if (presetId === CUSTOM_PRESET_ID) return null;
  const preset = TASK_REMINDER_PRESETS.find((p) => p.id === presetId);
  return preset ? preset.resolveAt() : null;
}

const TaskReminderSection: React.FC<TaskReminderSectionProps> = ({
  reminders,
  onCreateReminder,
  onCancelReminder,
}) => {
  const initialPresetId = defaultPresetId();
  const initialAt = resolvePresetDatetime(initialPresetId) ?? new Date(Date.now() + 3600000);

  const [presetId, setPresetId] = useState(initialPresetId);
  const [remindAtLocal, setRemindAtLocal] = useState(() => toDatetimeLocalValue(initialAt));
  const [message, setMessage] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [recurringIntervalId, setRecurringIntervalId] = useState(
    () => TASK_REMINDER_RECURRING_INTERVALS[2]?.id ?? '1d',
  );
  const [recurringPresetId, setRecurringPresetId] = useState(initialPresetId);
  const [recurringStartLocal, setRecurringStartLocal] = useState(() => toDatetimeLocalValue(initialAt));
  const [recurringMessage, setRecurringMessage] = useState('');
  const [addingRecurring, setAddingRecurring] = useState(false);
  const [recurringError, setRecurringError] = useState<string | null>(null);
  const [recurringSuccessMessage, setRecurringSuccessMessage] = useState<string | null>(null);
  const [composerMode, setComposerMode] = useState<'once' | 'recurring'>('once');

  const isCustom = presetId === CUSTOM_PRESET_ID;
  const isRecurringCustom = recurringPresetId === CUSTOM_PRESET_ID;
  const scheduledPreview = useMemo(() => {
    const d = new Date(remindAtLocal);
    if (Number.isNaN(d.getTime())) return '—';
    return format(d, 'EEE, MMM d · h:mm a');
  }, [remindAtLocal]);
  const recurringStartPreview = useMemo(() => {
    const d = new Date(recurringStartLocal);
    if (Number.isNaN(d.getTime())) return '—';
    return format(d, 'EEE, MMM d · h:mm a');
  }, [recurringStartLocal]);

  const pendingReminders = useMemo(
    () => reminders.filter((r) => r.status === 'pending'),
    [reminders],
  );
  const pendingOnce = useMemo(
    () => pendingReminders.filter((r) => r.kind !== 'recurring'),
    [pendingReminders],
  );
  const pendingRecurring = useMemo(
    () => pendingReminders.filter((r) => r.kind === 'recurring'),
    [pendingReminders],
  );
  const sentReminders = useMemo(
    () =>
      reminders
        .filter((r) => r.status === 'sent' && r.kind !== 'recurring')
        .sort((a, b) => (b.sentAt?.getTime() ?? 0) - (a.sentAt?.getTime() ?? 0))
        .slice(0, 3),
    [reminders],
  );

  useEffect(() => {
    if (!successMessage) return;
    const timer = window.setTimeout(() => setSuccessMessage(null), SUCCESS_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [successMessage]);

  useEffect(() => {
    if (!recurringSuccessMessage) return;
    const timer = window.setTimeout(() => setRecurringSuccessMessage(null), SUCCESS_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [recurringSuccessMessage]);

  useEffect(() => {
    const pending = reminders.filter((r) => r.status === 'pending');
    if (!pending.length) return;

    const maybeFlush = () => {
      const now = Date.now();
      if (pending.some((r) => r.remindAt.getTime() <= now)) {
        void flushDueTaskReminders();
      }
    };

    maybeFlush();
    const interval = window.setInterval(maybeFlush, 15000);
    return () => window.clearInterval(interval);
  }, [reminders]);

  const handlePresetChange = (nextId: string) => {
    setPresetId(nextId);
    setError(null);
    setSuccessMessage(null);
    if (nextId === CUSTOM_PRESET_ID) return;
    const resolved = resolvePresetDatetime(nextId);
    if (resolved) setRemindAtLocal(toDatetimeLocalValue(resolved));
  };

  const handleDatetimeChange = (value: string) => {
    setRemindAtLocal(value);
    setPresetId(CUSTOM_PRESET_ID);
    setError(null);
    setSuccessMessage(null);
  };

  const handleRecurringPresetChange = (nextId: string) => {
    setRecurringPresetId(nextId);
    setRecurringError(null);
    setRecurringSuccessMessage(null);
    if (nextId === CUSTOM_PRESET_ID) return;
    const resolved = resolvePresetDatetime(nextId);
    if (resolved) setRecurringStartLocal(toDatetimeLocalValue(resolved));
  };

  const handleRecurringStartChange = (value: string) => {
    setRecurringStartLocal(value);
    setRecurringPresetId(CUSTOM_PRESET_ID);
    setRecurringError(null);
    setRecurringSuccessMessage(null);
  };

  const resetForm = () => {
    const nextPreset = defaultPresetId();
    const resolved = resolvePresetDatetime(nextPreset) ?? new Date(Date.now() + 3600000);
    setPresetId(nextPreset);
    setRemindAtLocal(toDatetimeLocalValue(resolved));
    setMessage('');
  };

  const resetRecurringForm = () => {
    const nextPreset = defaultPresetId();
    const resolved = resolvePresetDatetime(nextPreset) ?? new Date(Date.now() + 3600000);
    setRecurringPresetId(nextPreset);
    setRecurringStartLocal(toDatetimeLocalValue(resolved));
    setRecurringMessage('');
  };

  const handleAdd = async () => {
    if (adding) return;
    setError(null);
    setSuccessMessage(null);
    const remindAt = new Date(remindAtLocal);
    if (Number.isNaN(remindAt.getTime())) {
      setError('Enter a valid date and time.');
      return;
    }
    if (!isTaskReminderInFuture(remindAt)) {
      setError('Reminder must be in the future.');
      return;
    }
    setAdding(true);
    try {
      await onCreateReminder(remindAt, message.trim() || undefined, { kind: 'once' });
      setSuccessMessage(`Reminder set for ${format(remindAt, 'EEE, MMM d · h:mm a')}.`);
      resetForm();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save reminder.');
    } finally {
      setAdding(false);
    }
  };

  const handleAddRecurring = async () => {
    if (addingRecurring) return;
    setRecurringError(null);
    setRecurringSuccessMessage(null);
    const remindAt = new Date(recurringStartLocal);
    if (Number.isNaN(remindAt.getTime())) {
      setRecurringError('Enter a valid start date and time.');
      return;
    }
    if (!isTaskReminderInFuture(remindAt)) {
      setRecurringError('First reminder must be in the future.');
      return;
    }
    const interval = TASK_REMINDER_RECURRING_INTERVALS.find((i) => i.id === recurringIntervalId);
    if (!interval) {
      setRecurringError('Choose a recurring interval.');
      return;
    }
    setAddingRecurring(true);
    try {
      await onCreateReminder(remindAt, recurringMessage.trim() || undefined, {
        kind: 'recurring',
        intervalMinutes: interval.minutes,
      });
      setRecurringSuccessMessage(
        `${interval.label} starting ${format(remindAt, 'EEE, MMM d · h:mm a')}.`,
      );
      resetRecurringForm();
    } catch (e) {
      setRecurringError(e instanceof Error ? e.message : 'Could not save recurring reminder.');
    } finally {
      setAddingRecurring(false);
    }
  };

  return (
    <div className="pm-reminder-section">
      <h4 className="pm-detail-section-title">
        Reminders
        {pendingReminders.length > 0 && (
          <span className="pm-detail-section-count">{pendingReminders.length}</span>
        )}
      </h4>
      <p className="pm-reminder-hint">
        Bell for employees · email for architects and engineers.
      </p>

      <div className="pm-reminder-mode-tabs" role="tablist" aria-label="Reminder type">
        <button
          type="button"
          role="tab"
          aria-selected={composerMode === 'once'}
          className={`pm-reminder-mode-tab ${composerMode === 'once' ? 'is-active' : ''}`}
          onClick={() => setComposerMode('once')}
        >
          One-time
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={composerMode === 'recurring'}
          className={`pm-reminder-mode-tab ${composerMode === 'recurring' ? 'is-active' : ''}`}
          onClick={() => setComposerMode('recurring')}
        >
          Recurring
        </button>
      </div>

      {composerMode === 'once' ? (
      <div className="pm-reminder-composer">
        <div className="pm-reminder-schedule-row">
          <label className="pm-reminder-field">
            <span className="pm-reminder-field-label">When</span>
            <select
              className="pm-inline-select pm-reminder-control"
              value={presetId}
              disabled={adding}
              onChange={(e) => handlePresetChange(e.target.value)}
              aria-label="Reminder preset"
            >
              {TASK_REMINDER_PRESETS.map((preset) => (
                <option key={preset.id} value={preset.id}>
                  {preset.label}
                </option>
              ))}
              <option value={CUSTOM_PRESET_ID}>Custom date &amp; time…</option>
            </select>
          </label>

          <label className="pm-reminder-field">
            <span className="pm-reminder-field-label">
              {isCustom ? 'Pick date & time' : 'Scheduled for'}
            </span>
            {isCustom ? (
              <input
                type="datetime-local"
                className="pm-inline-input pm-reminder-control"
                value={remindAtLocal}
                disabled={adding}
                onChange={(e) => handleDatetimeChange(e.target.value)}
                aria-label="Reminder date and time"
              />
            ) : (
              <div className="pm-reminder-datetime-preview" aria-live="polite">
                {scheduledPreview}
              </div>
            )}
          </label>
        </div>

        <label className="pm-reminder-field pm-reminder-field--note">
          <span className="pm-reminder-field-label">Note (optional)</span>
          <input
            type="text"
            className="pm-inline-input pm-reminder-control"
            placeholder="What should this reminder be about?"
            value={message}
            disabled={adding}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleAdd();
            }}
          />
        </label>

        {error && <p className="pm-reminder-feedback pm-reminder-feedback--error">{error}</p>}
        {successMessage && (
          <p className="pm-reminder-feedback pm-reminder-feedback--success" role="status">
            <Check size={14} aria-hidden />
            {successMessage}
          </p>
        )}

        <button
          type="button"
          className="pm-btn pm-btn-primary pm-reminder-submit"
          disabled={adding}
          onClick={() => void handleAdd()}
        >
          <Bell size={15} strokeWidth={2.25} />
          {adding ? 'Setting reminder…' : 'Remind me'}
        </button>
      </div>
      ) : (
      <div className="pm-reminder-composer pm-reminder-composer--recurring">
        <p className="pm-reminder-recurring-hint">
          Repeats at your chosen interval until this task is marked complete.
        </p>

        <div className="pm-reminder-schedule-row">
          <label className="pm-reminder-field">
            <span className="pm-reminder-field-label">Every</span>
            <select
              className="pm-inline-select pm-reminder-control"
              value={recurringIntervalId}
              disabled={addingRecurring}
              onChange={(e) => {
                setRecurringIntervalId(e.target.value);
                setRecurringError(null);
                setRecurringSuccessMessage(null);
              }}
              aria-label="Recurring interval"
            >
              {TASK_REMINDER_RECURRING_INTERVALS.map((interval) => (
                <option key={interval.id} value={interval.id}>
                  {interval.label}
                </option>
              ))}
            </select>
          </label>

          <label className="pm-reminder-field">
            <span className="pm-reminder-field-label">First reminder</span>
            <select
              className="pm-inline-select pm-reminder-control"
              value={recurringPresetId}
              disabled={addingRecurring}
              onChange={(e) => handleRecurringPresetChange(e.target.value)}
              aria-label="First reminder preset"
            >
              {TASK_REMINDER_PRESETS.map((preset) => (
                <option key={preset.id} value={preset.id}>
                  {preset.label}
                </option>
              ))}
              <option value={CUSTOM_PRESET_ID}>Custom date &amp; time…</option>
            </select>
          </label>
        </div>

        <label className="pm-reminder-field">
          <span className="pm-reminder-field-label">
            {isRecurringCustom ? 'Pick start date & time' : 'Starts'}
          </span>
          {isRecurringCustom ? (
            <input
              type="datetime-local"
              className="pm-inline-input pm-reminder-control"
              value={recurringStartLocal}
              disabled={addingRecurring}
              onChange={(e) => handleRecurringStartChange(e.target.value)}
              aria-label="Recurring reminder start time"
            />
          ) : (
            <div className="pm-reminder-datetime-preview" aria-live="polite">
              {recurringStartPreview}
            </div>
          )}
        </label>

        <label className="pm-reminder-field pm-reminder-field--note">
          <span className="pm-reminder-field-label">Note (optional)</span>
          <input
            type="text"
            className="pm-inline-input pm-reminder-control"
            placeholder="What should this reminder be about?"
            value={recurringMessage}
            disabled={addingRecurring}
            onChange={(e) => setRecurringMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleAddRecurring();
            }}
          />
        </label>

        {recurringError && (
          <p className="pm-reminder-feedback pm-reminder-feedback--error">{recurringError}</p>
        )}
        {recurringSuccessMessage && (
          <p className="pm-reminder-feedback pm-reminder-feedback--success" role="status">
            <Check size={14} aria-hidden />
            {recurringSuccessMessage}
          </p>
        )}

        <button
          type="button"
          className="pm-btn pm-reminder-submit pm-reminder-submit--recurring"
          disabled={addingRecurring}
          onClick={() => void handleAddRecurring()}
        >
          <Repeat size={15} strokeWidth={2.25} />
          {addingRecurring ? 'Starting…' : 'Start recurring'}
        </button>
      </div>
      )}

      {pendingOnce.length > 0 && (
        <ul className="pm-reminder-list">
          {pendingOnce.map((r) => (
            <li key={r.id} className="pm-reminder-item pm-reminder-item--pending">
              <div className="pm-reminder-item-main">
                <Clock size={13} className="pm-reminder-item-icon" aria-hidden />
                <div className="pm-reminder-item-body">
                  <span className="pm-reminder-item-when">
                    {format(r.remindAt, 'MMM d, yyyy h:mm a')}
                    <span className="pm-reminder-item-relative">
                      ({formatDistanceToNow(r.remindAt, { addSuffix: true })})
                    </span>
                  </span>
                  {r.message?.trim() ? (
                    <span className="pm-reminder-item-note">{r.message}</span>
                  ) : null}
                </div>
              </div>
              {r.id && (
                <button
                  type="button"
                  className="pm-btn-ghost pm-btn-sm pm-reminder-cancel"
                  title="Cancel reminder"
                  aria-label="Cancel reminder"
                  onClick={() => onCancelReminder(r.id!)}
                >
                  <X size={12} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {pendingRecurring.length > 0 && (
        <ul className="pm-reminder-list pm-reminder-list--recurring">
          {pendingRecurring.map((r) => (
            <li key={r.id} className="pm-reminder-item pm-reminder-item--recurring">
              <div className="pm-reminder-item-main">
                <Repeat size={13} className="pm-reminder-item-icon" aria-hidden />
                <div className="pm-reminder-item-body">
                  <span className="pm-reminder-item-when">
                    {r.intervalMinutes
                      ? formatRecurringIntervalLabel(r.intervalMinutes)
                      : 'Recurring'}
                    <span className="pm-reminder-item-relative">
                      · next {format(r.remindAt, 'MMM d, h:mm a')}
                    </span>
                  </span>
                  {r.lastSentAt ? (
                    <span className="pm-reminder-item-note">
                      Last sent {formatDistanceToNow(r.lastSentAt, { addSuffix: true })}
                    </span>
                  ) : null}
                  {r.message?.trim() ? (
                    <span className="pm-reminder-item-note">{r.message}</span>
                  ) : null}
                </div>
              </div>
              {r.id && (
                <button
                  type="button"
                  className="pm-btn-ghost pm-btn-sm pm-reminder-cancel"
                  title="Stop recurring reminder"
                  aria-label="Stop recurring reminder"
                  onClick={() => onCancelReminder(r.id!)}
                >
                  <X size={12} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {sentReminders.length > 0 && (
        <ul className="pm-reminder-list pm-reminder-list--sent">
          {sentReminders.map((r) => (
            <li key={r.id} className="pm-reminder-item pm-reminder-item--sent">
              <Bell size={13} className="pm-reminder-item-icon" aria-hidden />
              <div className="pm-reminder-item-body">
                <span className="pm-reminder-item-when">
                  Sent {r.sentAt ? formatDistanceToNow(r.sentAt, { addSuffix: true }) : 'recently'}
                </span>
                {r.message?.trim() ? (
                  <span className="pm-reminder-item-note">{r.message}</span>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TaskReminderSection;
